import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { errorMessage, responseTexts } from '@helpers/Constants';
import { GiftBoxIcon } from '@helpers/Constants/Icons';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { Subscription } from '@helpers/Models';
import { handleDateFormat } from '@services/OrderCreationService';
import { useEffect, useState } from 'react';

export const ButcherSubscriptionDetails = () => {
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();

  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  // Get the expired pomo codes
  const expiredPromos = subscriptionDetails?.promoCodes?.filter(
    (promo) => promo.isActive !== 1,
  );

  // Get the current active promo codes
  const activePromos = subscriptionDetails?.promoCodes?.filter(
    (promo) => promo.isActive === 1,
  );

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentConfirmation = (id: number): void => {
    showConfirmationToast(
      'Are you sure you want to update your payment method?',
      'Confirm Payment Update',
      'info',
      () => handleUpdatePayment(id),
      'Proceed',
      () => {}, // cancel
      'Cancel',
    );
  };

  const handleUpdatePayment = async (id: number): Promise<void> => {
    try {
      const payload = {
        subscriptionId: id,
      };

      const response = await Axios.post(
        ApiEndpoints.UPDATE_PAYMENT_METHOD,
        payload,
      );

      // Storing the create checkout session data for future use - if the payment fails
      localStorage.setItem('createCheckoutSession', JSON.stringify(payload));

      // Navigating to the payment page
      window.location.href = response.data.data.checkoutUrl;

      localStorage.setItem('sessionId', response.data.data.sessionId);
      showToastMessage(responseTexts.REDIRECTING_PAYMENT, 'success');
    } catch (error: any) {
      showToastMessage(error.error.message, 'error');
    }
  };

  return (
    <div className={`p-2 ${loading ? 'opacity-50' : 'opacity-100'}`}>
      {/* Plan details */}
      <div className="border border-[#E8E8E8] p-4 mt-3 rounded-md bg-white shadow-sm">
        <div className="grid grid-cols-2 gap-x-8 pb-4 border-b border-[#F0F0F0]">
          <div className="space-y-4">
            {/* Billing Cycle */}
            <div>
              <p className="text-sm text-[#6B7280]">Billing Cycle</p>
              <p className="text-[#1A1A1A] font-medium mt-1">
                {subscriptionDetails?.billingCycle}
              </p>
            </div>

            {/* Billing Rate */}
            <div>
              <p className="text-sm text-[#6B7280]">Billing Rate</p>
              <p className="text-[#1A1A1A] font-semibold mt-1">
                ${subscriptionDetails?.price} per animal
              </p>
            </div>

            {/* Next Billing Date */}
            <div>
              <p className="text-sm text-[#6B7280]">Next Billing Date</p>
              <p className="text-[#1A1A1A] font-semibold mt-1">
                {handleDateFormat(subscriptionDetails?.nextBillingDate)}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Trial Period */}

            {/* Show trial period only for the trial period users */}
            {subscriptionDetails?.trialEndsAt != null && (
              <div>
                <p className="text-sm text-[#6B7280]">Trial Period</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-[#1A1A1A] font-medium">
                    {subscriptionDetails?.trialMaxDays} Days (Completed)
                  </p>

                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#DBEAFE]">
                    <GiftBoxIcon width={16} height={16} fill="#1E40AF" />
                    <span className="text-sm text-[#1E40AF] font-medium whitespace-nowrap">
                      The trial period has ended.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Animals Processed */}
            <div>
              <p className="text-sm text-[#6B7280]">
                Animals Processed This Month
              </p>
              <p className="text-[#1A1A1A] font-medium mt-1">
                {subscriptionDetails?.orderSpeciesCount} animals
              </p>
            </div>

            {/* Grace Period */}
            <div>
              <p className="text-sm text-[#6B7280]">Grace Period Overides</p>
              <p className="text-[#1A1A1A] font-semibold mt-1">
                {subscriptionDetails?.gracePeriodOverride} Overide
              </p>
            </div>
          </div>
        </div>
        {/* Payment Method */}
        <div className="border-b border-[#F0F0F0]">
          <div className="grid grid-cols-1 gap-3">
            <div className="bg-white py-3">
              <div className="flex items-center justify-between gap-2.5 mb-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="p-2 bg-[#FEF3C7] rounded-md">
                    <svg
                      width="20"
                      height="16"
                      viewBox="0 0 20 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2ZM2 4H18V2H2V4ZM2 8V14H18V8H2Z"
                        fill="#F59E0B"
                      />
                    </svg>
                  </span>
                  <span className="text-sm text-[#374151] font-medium">
                    Payment Method
                  </span>
                </div>
                <button
                  onClick={() => {
                    handleUpdatePaymentConfirmation(
                      subscriptionDetails?.subscriptionPlanId ?? 1,
                    );
                  }}
                  type="button"
                  className="px-6 py-1.5 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Update Payment Method
                </button>
              </div>
              <div className="flex items-baseline gap-6 mt-3">
                <span className="text-[20px] font-bold text-[#1A1A1A]">
                  •••• {subscriptionDetails?.paymentMethod}
                </span>
                <span className="text-sm text-[#6B7280]">
                  {subscriptionDetails?.paymentBrand} ending in{' '}
                  {subscriptionDetails?.paymentMethod}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Promo code */}
        <div className="grid grid-cols-2 gap-x-8 pt-4 pb-4">
          {/* Expired Promo */}
          {expiredPromos && expiredPromos?.length > 0 && (
            <div>
              <p className="text-sm text-[#6B7280] font-medium">
                Expired Promo Codes
              </p>

              {subscriptionDetails?.promoCodes
                ?.filter((promo) => promo.isActive !== 1)
                .map((promo) => (
                  <div key={promo.id} className="mt-2">
                    <p className="text-[#1A1A1A] font-bold">
                      {promo.PromoCodes.code}
                    </p>
                    <p className="text-sm text-[#6B7280] mt-0.5">
                      {promo.PromoCodes.description}
                    </p>
                  </div>
                ))}
            </div>
          )}

          {/* Active Promo */}
          {activePromos && activePromos.length > 0 && (
            <div>
              <p className="text-sm text-[#DBB145] font-medium">
                Active Promo Code
              </p>

              {subscriptionDetails?.promoCodes
                ?.filter((promo) => promo.isActive === 1)
                .map((promo) => (
                  <div key={promo.id} className="mt-2">
                    <p className="text-[#1A1A1A] font-bold">
                      {promo.PromoCodes.code}
                    </p>
                    <p className="text-sm text-[#6B7280] mt-0.5">
                      {promo.PromoCodes.description}
                    </p>

                    <div className="flex items-center gap-2 mt-2 bg-[#F8F8F8] rounded-md px-3 py-2">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#9CA3AF"
                          strokeWidth="2"
                        />
                        <path
                          d="M12 16v-4"
                          stroke="#9CA3AF"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <circle cx="12" cy="8" r="1" fill="#899197" />
                      </svg>
                      <span className="text-sm text-[#4B5563]">
                        This promo code was provided by WGB.
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Dashboard details */}
      <div className="mt-3">
        {/* Animals This Billing Period*/}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Animals card */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-2.5 mb-2.5">
              <span className="p-2 bg-[#EDE9FE] rounded-md">
                <svg
                  width="22"
                  height="20"
                  viewBox="0 0 22 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.5264 8.1582C10.9931 8.15821 11.414 8.26604 11.8027 8.48047C12.2249 8.71337 12.5982 9.01951 12.9238 9.4043L12.9326 9.41504C13.3901 9.93793 13.7947 10.502 14.1465 11.1074C14.5517 11.8047 15.0278 12.4547 15.5732 13.0566L15.5771 13.0615C16.1901 13.727 16.808 14.401 17.4297 15.084C17.9221 15.6251 18.1582 16.2347 18.1582 16.9473C18.1582 17.5835 17.9583 18.1117 17.5537 18.5693C17.1646 19.0095 16.7153 19.2109 16.1582 19.2109C15.255 19.2109 14.3605 19.1348 13.4746 18.9844C12.5009 18.819 11.5178 18.7373 10.5264 18.7373C9.53491 18.7373 8.55181 18.819 7.57812 18.9844C6.69221 19.1348 5.79776 19.2109 4.89453 19.2109C4.33736 19.2109 3.8882 19.0096 3.49902 18.5693C3.09444 18.1117 2.89453 17.5835 2.89453 16.9473C2.89455 16.2347 3.13056 15.6251 3.62305 15.084C4.24481 14.4009 4.86166 13.726 5.47461 13.0605C6.02269 12.474 6.49664 11.8351 6.89453 11.1436C7.23411 10.5533 7.63947 9.99821 8.11133 9.47754L8.12012 9.46777L8.12891 9.45703C8.45793 9.06821 8.83694 8.74954 9.26758 8.49707C9.65382 8.27065 10.0685 8.1582 10.5264 8.1582ZM2.63184 5C3.16481 5.00006 3.5847 5.17553 3.94141 5.53223C4.29811 5.88893 4.47357 6.30883 4.47363 6.8418C4.47363 7.37491 4.29818 7.79557 3.94141 8.15234C3.58474 8.50892 3.16472 8.68451 2.63184 8.68457C2.09873 8.68457 1.67806 8.50911 1.32129 8.15234C0.964537 7.79558 0.789062 7.37489 0.789062 6.8418C0.78913 6.30894 0.964727 5.88888 1.32129 5.53223C1.67806 5.17546 2.09873 5 2.63184 5ZM18.4209 5C18.954 5 19.3747 5.17546 19.7314 5.53223C20.088 5.88887 20.2626 6.30896 20.2627 6.8418C20.2627 7.37487 20.0882 7.79559 19.7314 8.15234C19.3747 8.50911 18.954 8.68457 18.4209 8.68457C17.888 8.68454 17.468 8.50891 17.1113 8.15234C16.7546 7.79557 16.5791 7.37491 16.5791 6.8418C16.5792 6.30883 16.7546 5.88893 17.1113 5.53223C17.468 5.17554 17.8879 5.00003 18.4209 5ZM7.36816 0.789062C7.90127 0.789062 8.32194 0.96452 8.67871 1.32129C9.03548 1.67806 9.21094 2.09873 9.21094 2.63184C9.21088 3.16471 9.03527 3.58474 8.67871 3.94141C8.32194 4.29818 7.90127 4.47363 7.36816 4.47363C6.83518 4.47358 6.41531 4.29812 6.05859 3.94141C5.70188 3.58469 5.52642 3.16482 5.52637 2.63184C5.52637 2.09873 5.70182 1.67806 6.05859 1.32129C6.41526 0.964733 6.83529 0.789119 7.36816 0.789062ZM13.6846 0.789062C14.2174 0.789141 14.6375 0.964721 14.9941 1.32129C15.3509 1.67806 15.5264 2.09873 15.5264 2.63184C15.5263 3.1648 15.3508 3.58471 14.9941 3.94141C14.6375 4.2981 14.2175 4.47355 13.6846 4.47363C13.1515 4.47363 12.7308 4.29818 12.374 3.94141C12.0174 3.58473 11.8419 3.16473 11.8418 2.63184C11.8418 2.09873 12.0173 1.67806 12.374 1.32129C12.7308 0.964554 13.1515 0.789062 13.6846 0.789062Z"
                    stroke="#8B5CF6"
                    strokeWidth="1.57895"
                  />
                </svg>
              </span>
              <span className="text-sm text-[#374151] font-medium">
                Animals This Billing Period
              </span>
            </div>
            <div className="flex items-baseline gap-6 mt-3">
              <span className="text-[28px] font-bold text-[#1A1A1A]">
                {subscriptionDetails?.orderSpeciesCount}
              </span>
              <span className="text-sm text-[#6B7280]">Animals</span>
            </div>
          </div>
          {/* Estimated Costs card */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-2.5 mb-2.5">
              <span className="p-2 bg-[#D1FAE5] rounded-md">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.27902 13.598H8.67902V12.598C9.34569 12.478 9.91902 12.218 10.399 11.818C10.879 11.418 11.119 10.8247 11.119 10.038C11.119 9.47805 10.959 8.96471 10.639 8.49805C10.319 8.03138 9.67902 7.62471 8.71902 7.27805C7.91902 7.01138 7.36569 6.77805 7.05902 6.57805C6.75236 6.37805 6.59902 6.10471 6.59902 5.75805C6.59902 5.41138 6.72236 5.13805 6.96902 4.93805C7.21569 4.73805 7.57236 4.63805 8.03902 4.63805C8.46569 4.63805 8.79902 4.74138 9.03902 4.94805C9.27902 5.15471 9.45236 5.41138 9.55902 5.71805L10.839 5.19805C10.6924 4.73138 10.4224 4.32471 10.029 3.97805C9.63569 3.63138 9.19902 3.43805 8.71902 3.39805V2.39805H7.31902V3.39805C6.65236 3.54471 6.13236 3.83805 5.75902 4.27805C5.38569 4.71805 5.19902 5.21138 5.19902 5.75805C5.19902 6.38471 5.38236 6.89138 5.74902 7.27805C6.11569 7.66471 6.69236 7.99805 7.47902 8.27805C8.31902 8.58471 8.90236 8.85805 9.22902 9.09805C9.55569 9.33805 9.71902 9.65138 9.71902 10.038C9.71902 10.478 9.56236 10.8014 9.24902 11.008C8.93569 11.2147 8.55902 11.318 8.11902 11.318C7.67902 11.318 7.28902 11.1814 6.94902 10.908C6.60902 10.6347 6.35902 10.2247 6.19902 9.67805L4.87902 10.198C5.06569 10.838 5.35569 11.3547 5.74902 11.748C6.14236 12.1414 6.65236 12.4114 7.27902 12.558V13.598ZM7.99902 15.998C6.89236 15.998 5.85236 15.788 4.87902 15.368C3.90569 14.948 3.05902 14.378 2.33902 13.658C1.61902 12.938 1.04902 12.0914 0.629023 11.118C0.209023 10.1447 -0.000976562 9.10471 -0.000976562 7.99805C-0.000976562 6.89138 0.209023 5.85138 0.629023 4.87805C1.04902 3.90471 1.61902 3.05805 2.33902 2.33805C3.05902 1.61805 3.90569 1.04805 4.87902 0.628047C5.85236 0.208047 6.89236 -0.00195312 7.99902 -0.00195312C9.10569 -0.00195312 10.1457 0.208047 11.119 0.628047C12.0924 1.04805 12.939 1.61805 13.659 2.33805C14.379 3.05805 14.949 3.90471 15.369 4.87805C15.789 5.85138 15.999 6.89138 15.999 7.99805C15.999 9.10471 15.789 10.1447 15.369 11.118C14.949 12.0914 14.379 12.938 13.659 13.658C12.939 14.378 12.0924 14.948 11.119 15.368C10.1457 15.788 9.10569 15.998 7.99902 15.998ZM7.99902 14.398C9.78569 14.398 11.299 13.778 12.539 12.538C13.779 11.298 14.399 9.78471 14.399 7.99805C14.399 6.21138 13.779 4.69805 12.539 3.45805C11.299 2.21805 9.78569 1.59805 7.99902 1.59805C6.21236 1.59805 4.69902 2.21805 3.45902 3.45805C2.21902 4.69805 1.59902 6.21138 1.59902 7.99805C1.59902 9.78471 2.21902 11.298 3.45902 12.538C4.69902 13.778 6.21236 14.398 7.99902 14.398Z"
                    fill="#009362"
                  />
                </svg>
              </span>
              <span className="text-sm text-[#374151] font-medium">
                Utilized Cost for This Month
              </span>
            </div>
            <div className="flex items-baseline gap-6 mt-3">
              <span className="text-[24px] font-bold text-[#1A1A1A]">
                ${subscriptionDetails?.estimatedCost}
              </span>
              <span className="text-sm text-[#6B7280]">
                {subscriptionDetails?.orderSpeciesCount} animals × $
                {subscriptionDetails?.price} per animal
              </span>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Next Billing Date */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-2.5 mb-2.5">
              <span className="p-2 bg-[#FEE2E2] rounded-md">
                <svg
                  width="22"
                  height="16"
                  viewBox="0 0 22 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 9C12.1667 9 11.4583 8.70833 10.875 8.125C10.2917 7.54167 10 6.83333 10 6C10 5.16667 10.2917 4.45833 10.875 3.875C11.4583 3.29167 12.1667 3 13 3C13.8333 3 14.5417 3.29167 15.125 3.875C15.7083 4.45833 16 5.16667 16 6C16 6.83333 15.7083 7.54167 15.125 8.125C14.5417 8.70833 13.8333 9 13 9ZM6 12C5.45 12 4.97917 11.8042 4.5875 11.4125C4.19583 11.0208 4 10.55 4 10V2C4 1.45 4.19583 0.979167 4.5875 0.5875C4.97917 0.195833 5.45 0 6 0H20C20.55 0 21.0208 0.195833 21.4125 0.5875C21.8042 0.979167 22 1.45 22 2V10C22 10.55 21.8042 11.0208 21.4125 11.4125C21.0208 11.8042 20.55 12 20 12H6ZM8 10H18C18 9.45 18.1958 8.97917 18.5875 8.5875C18.9792 8.19583 19.45 8 20 8V4C19.45 4 18.9792 3.80417 18.5875 3.4125C18.1958 3.02083 18 2.55 18 2H8C8 2.55 7.80417 3.02083 7.4125 3.4125C7.02083 3.80417 6.55 4 6 4V8C6.55 8 7.02083 8.19583 7.4125 8.5875C7.80417 8.97917 8 9.45 8 10ZM19 16H2C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V3H2V14H19V16Z"
                    fill="#DC2626"
                  />
                </svg>
              </span>
              <span className="text-sm text-[#374151] font-medium">
                Next Billing Date
              </span>
            </div>
            <div className="flex items-baseline gap-6 mt-3">
              <span className="text-[20px] font-bold text-[#1A1A1A]">
                {handleDateFormat(subscriptionDetails?.nextBillingDate)}
              </span>
            </div>
          </div>
          {/* Last Payment card */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-2.5 mb-2.5">
              <span className="p-2 bg-[#DBEAFE] rounded-md">
                <svg
                  width="20"
                  height="17"
                  viewBox="0 0 20 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 0H1.5C1.10218 0 0.720644 0.158035 0.43934 0.43934C0.158035 0.720644 0 1.10218 0 1.5V15.75C6.82428e-05 15.8778 0.0328074 16.0035 0.0951092 16.1151C0.157411 16.2268 0.247208 16.3206 0.355974 16.3878C0.46474 16.4549 0.588867 16.4932 0.716569 16.4989C0.844271 16.5046 0.97131 16.4775 1.08563 16.4203L3.75 15.0881L6.41437 16.4203C6.51857 16.4725 6.63348 16.4996 6.75 16.4996C6.86652 16.4996 6.98143 16.4725 7.08563 16.4203L9.75 15.0881L12.4144 16.4203C12.5186 16.4725 12.6335 16.4996 12.75 16.4996C12.8665 16.4996 12.9814 16.4725 13.0856 16.4203L15.75 15.0881L18.4144 16.4203C18.5287 16.4775 18.6557 16.5046 18.7834 16.4989C18.9111 16.4932 19.0353 16.4549 19.144 16.3878C19.2528 16.3206 19.3426 16.2268 19.4049 16.1151C19.4672 16.0035 19.4999 15.8778 19.5 15.75V1.5C19.5 1.10218 19.342 0.720644 19.0607 0.43934C18.7794 0.158035 18.3978 0 18 0ZM14.25 9.75H5.25C5.05109 9.75 4.86032 9.67098 4.71967 9.53033C4.57902 9.38968 4.5 9.19891 4.5 9C4.5 8.80109 4.57902 8.61032 4.71967 8.46967C4.86032 8.32902 5.05109 8.25 5.25 8.25H14.25C14.4489 8.25 14.6397 8.32902 14.7803 8.46967C14.921 8.61032 15 8.80109 15 9C15 9.19891 14.921 9.38968 14.7803 9.53033C14.6397 9.67098 14.4489 9.75 14.25 9.75ZM14.25 6.75H5.25C5.05109 6.75 4.86032 6.67098 4.71967 6.53033C4.57902 6.38968 4.5 6.19891 4.5 6C4.5 5.80109 4.57902 5.61032 4.71967 5.46967C4.86032 5.32902 5.05109 5.25 5.25 5.25H14.25C14.4489 5.25 14.6397 5.32902 14.7803 5.46967C14.921 5.61032 15 5.80109 15 6C15 6.19891 14.921 6.38968 14.7803 6.53033C14.6397 6.67098 14.4489 6.75 14.25 6.75Z"
                    fill="#3B82F6"
                  />
                </svg>
              </span>
              <span className="text-sm text-[#374151] font-medium">
                Last Payment
              </span>
            </div>
            <div className="flex items-baseline gap-6 mt-3">
              <span className="text-[24px] font-bold text-[#1A1A1A]">
                ${subscriptionDetails?.lastMonthPayment}
              </span>
              <span className="text-sm text-[#6B7280]">
                Paid on {handleDateFormat(subscriptionDetails?.lastPaymentDate)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Method */}
      </div>
    </div>
  );
};
